// v3 storage, authored the way age-of-mcp writes it: local field helpers + the
// shared styleV3 part makers, instead of a flat wall of literals. A WORKING
// GOODS YARD, read from above: a plank barn under a big terracotta gable fills
// the WEST half, and the EAST half is an OPEN yard where the identity lives —
// oversized stacked crates, fat barrels and canvas sacks standing in the open.
// Big double doors on the barn's street face (+z). Ported 1:1 from age-of-mcp's
// v3/storage.ts.
import { defineModel, styleV3, type Part } from "../../lib";

const { WOOD, WOOD_DK, TIMBER, CANVAS, ROOF_HOME, box, cyl, slab, poles, gable } = styleV3;

const BASE   = 0.12;
const BX     = -0.45;        // the barn sits on the west half of the plot
const BARN_W = 0.95;
const BARN_D = 1.5;
const WALL_H = 0.9;
const FRONT  = BARN_D / 2;   // the barn's door face (+z)

// The plank barn body, `h` tall on the foundation.
function barn(h: number): Part {
  return box(BARN_W, h, BARN_D, WOOD, [BX, BASE + h / 2, 0]);
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [slab(1.94, 1.94)];
  if (s === 1) return [slab(1.94, 1.94), barn(0.4), ...poles(1.9, 1.9, 0.85)];
  if (s === 2) return [slab(1.94, 1.94), barn(WALL_H), ...poles(1.9, 1.9, 1.5)];

  const top = BASE + WALL_H;          // 1.02 — the eave line
  return [
    slab(1.94, 1.94),
    barn(WALL_H),
    // Big terracotta gable over the barn — ridge along z. ONE part.
    gable(1.12, 1.62, 0.55, ROOF_HOME, BX, top, 0, "z"),
    // Wide double doors with an oak lintel on the street face.
    box(0.55, 0.62, 0.05, WOOD_DK, [BX, BASE + 0.31, FRONT + 0.01]),
    box(0.62, 0.06, 0.06, TIMBER,  [BX, BASE + 0.65, FRONT + 0.01]),
    box(0.05, 0.62, 0.06, TIMBER,  [BX, BASE + 0.31, FRONT + 0.015]),
    // THE YARD (east half, open to the sky): the goods are the sign.
    // A stacked crate tower…
    box(0.4, 0.4, 0.4,    WOOD,    [0.42, BASE + 0.2, -0.5]),
    box(0.34, 0.34, 0.34, WOOD_DK, [0.42, BASE + 0.57, -0.5]),
    box(0.32, 0.32, 0.32, WOOD,    [0.8, BASE + 0.16, -0.42]),
    // …fat barrels…
    cyl(0.16, 0.15, 0.34, 8, WOOD_DK, [0.35, BASE + 0.17, 0.3]),
    cyl(0.14, 0.13, 0.3, 8,  WOOD_DK, [0.68, BASE + 0.15, 0.45]),
    // …and plump canvas sacks.
    cyl(0.13, 0.17, 0.24, 6, CANVAS, [0.76, BASE + 0.12, 0.02]),
    cyl(0.11, 0.15, 0.2, 6,  CANVAS, [0.5, BASE + 0.1, -0.08]),
  ];
}

export const storageV3 = defineModel({
  id: "storage-v3",
  tiles: { w: 2, h: 2 },
  batchId: "45-optimized",
  name: "Storage (v3)",
  tags: ["building","storage","low-poly","age-of-mcp","v3"],
  summary: "Low-poly storage.",
  description: "A walled yard of stacked crates under slanted plank roofs, holding far more goods than a chest.",
  author: "age-of-mcp",
  footprint: 1,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
});
